import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";

import { loginRequest, protectedResources } from "../authConfig";
import useAuthToken from "../hooks/useAuthToken";
import { createApiClient } from "../utils/apiClient";
import { useUser } from "../utils/userContext";
import type { ApiClient, TierLevel } from "../types/UserTypes";
import type {
  PlanPriceResponse,
  SignupCheckoutResponse,
  SignupReadyResponse,
  SignupStartRequest,
  SignupStartResponse,
  SignupStatusResponse,
} from "../types/apiSchemaAliases";
import {
  PRIVACY_VERSION,
  TERMS_VERSION,
} from "../legal/legalVersions";
import styles from "./Signup.module.css";

type SignupStep = "details" | "plans" | "checkout" | "pending";
type SignupTier = TierLevel;
type CheckoutSession = {
  clientSecret: string;
  publishableKey: string;
  signupId: string;
  selectedTier: SignupTier;
  seatQuantity: number;
};
type SignupDraft = {
  tenantName?: string;
  tenantSlug?: string;
  slugTouched?: boolean;
  selectedTier?: SignupTier;
  signupId?: string | null;
  step?: SignupStep;
  continueAfterSignIn?: boolean;
  legalAccepted?: boolean;
};

const SIGNUP_DRAFT_KEY = "signupDraft:v3";
const OLD_SIGNUP_DRAFT_KEYS = ["signupDraft:v2"];
const POLL_INTERVAL_MS = 2000;
const POLL_ATTEMPTS = 30;
const SIGNUP_STEPS: SignupStep[] = ["details", "plans", "checkout", "pending"];
const TIER_ORDER: SignupTier[] = ["free_crew", "core", "pro", "enterprise"];
const TIER_LABELS: Record<SignupTier, string> = {
  free_crew: "FreeCrew",
  core: "Core",
  pro: "Pro",
  enterprise: "Enterprise",
};
const TIER_DESCRIPTIONS: Record<SignupTier, string> = {
  free_crew: "Start small with essential time, expense, client, and prospect tools.",
  core: "Add operational billing features, approvals, invoice generation, and receipt uploads.",
  pro: "Unlock advanced controls, reporting, audit history, and custom configuration.",
  enterprise: "Full access for teams with custom fee structure or integration needs.",
};
const PLAN_FEATURES = [
  { label: "Time & Expense Entry", free_crew: true, core: true, pro: true, enterprise: true },
  { label: "Client & Prospect Coding", free_crew: true, core: true, pro: true, enterprise: true },
  { label: "Receipt Uploads", free_crew: false, core: true, pro: true, enterprise: true },
  { label: "Approval Workflow", free_crew: false, core: "Standard", pro: "Advanced", enterprise: "Advanced" },
  { label: "Invoice Generation", free_crew: false, core: true, pro: true, enterprise: true },
  { label: "Time Overrides", free_crew: false, core: false, pro: true, enterprise: true },
  { label: "Company Expense Accounts", free_crew: false, core: false, pro: true, enterprise: true },
  { label: "Client-Ready Reports", free_crew: false, core: false, pro: true, enterprise: true },
  { label: "Audit History", free_crew: false, core: false, pro: true, enterprise: true },
];

const isSignupTier = (value: unknown): value is SignupTier =>
  value === "free_crew" || value === "core" || value === "pro" || value === "enterprise";

const isPaidTier = (value: SignupTier): value is "core" | "pro" => value === "core" || value === "pro";

const slugify = (value: unknown) =>
  String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-")
    .replace(/[^a-z0-9-]/g, "")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

const delay = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const storage = {
  get: (key: string) => {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set: (key: string, value: string) => {
    try {
      window.localStorage.setItem(key, value);
    } catch {}
  },
  remove: (key: string) => {
    try {
      window.localStorage.removeItem(key);
    } catch {}
  },
};

function getErrorMessage(err: unknown, fallback = "Request failed.") {
  if (!err) return fallback;
  const apiErr = err as { details?: unknown; message?: string };
  const details = apiErr.details;
  if (typeof details === "string" && details.trim()) return details.replace(/^"|"$/g, "");
  if (details && typeof details === "object") {
    const record = details as Record<string, unknown>;
    if (typeof record.message === "string" && record.message.trim()) return record.message;
    if (typeof record.detail === "string" && record.detail.trim()) return record.detail;
    if (typeof record.title === "string" && record.title.trim()) return record.title;
    if (record.errors) return "Validation failed.";
  }
  return apiErr.message || fallback;
}

function loadDraft(): SignupDraft | null {
  try {
    const raw = storage.get(SIGNUP_DRAFT_KEY) ?? storage.get("signupDraft:v2");
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SignupDraft;
    return parsed && typeof parsed === "object" ? parsed : null;
  } catch {
    return null;
  }
}

function saveDraft(draft: SignupDraft) {
  storage.set(SIGNUP_DRAFT_KEY, JSON.stringify(draft));
}

function clearDraft() {
  storage.remove(SIGNUP_DRAFT_KEY);
  OLD_SIGNUP_DRAFT_KEYS.forEach((key) => storage.remove(key));
}

function formatMoney(amount: number | null | undefined, currency: string | null | undefined) {
  if (amount === null || amount === undefined) return null;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
  }).format(amount);
}

function formatPlanPrice(plan: PlanPriceResponse | undefined, tier: SignupTier, loading = false) {
  if (!plan && loading) return "Loading...";
  if (tier === "enterprise" || plan?.isContactSales) return "Contact sales";
  if (tier === "free_crew") return "Free";
  const base = formatMoney(plan?.baseMonthlyPrice, plan?.currency);
  if (!base) return "Contact us";
  return `${base}/mo`;
}

function formatSeatPrice(plan: PlanPriceResponse | undefined, tier: SignupTier, loading = false) {
  if (!plan && loading) return "Loading...";
  if (plan?.isContactSales || tier === "enterprise") return "Custom";
  if (tier === "free_crew") return "Free";
  const seat = formatMoney(plan?.seatMonthlyPrice, plan?.currency);
  return seat ? `${seat}/mo` : "--";
}

function renderFeatureValue(value: boolean | string) {
  if (value === true) {
    return (
      <span className={styles.check} aria-label="Included">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M3 8l3.5 3.5L13 5" stroke="#22c55e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </span>
    );
  }
  if (value === false) {
    return (
      <span className={styles.cross} aria-label="Not included">
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path d="M4 8h8" stroke="#d1d5db" strokeWidth="2" strokeLinecap="round" />
        </svg>
      </span>
    );
  }
  return value;
}

const Signup = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { instance } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const { token, getToken } = useAuthToken();
  const { setActiveTenantId } = useUser();

  const [tenantName, setTenantName] = useState("");
  const [tenantSlug, setTenantSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [selectedTier, setSelectedTier] = useState<SignupTier>("pro");
  const [signupId, setSignupId] = useState<string | null>(null);
  const [step, setStep] = useState<SignupStep>("details");
  const [prices, setPrices] = useState<PlanPriceResponse[]>([]);
  const [pricesLoading, setPricesLoading] = useState(true);
  const [pricesError, setPricesError] = useState("");
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [status, setStatus] = useState<SignupStatusResponse | null>(null);
  const [checkoutSession, setCheckoutSession] = useState<CheckoutSession | null>(null);
  const [hydrated, setHydrated] = useState(false);
  const [continueAfterSignIn, setContinueAfterSignIn] = useState(false);
  const [legalAccepted, setLegalAccepted] = useState(false);

  const baseUrl = protectedResources.baseURL.endpoint;
  const publicApi = useMemo(
    () => createApiClient({ baseUrl, token: null, tenantId: null }),
    [baseUrl],
  );
  const api = useMemo<ApiClient | null>(() => {
    if (!token) return null;
    return createApiClient({ baseUrl, token, getToken, tenantId: null });
  }, [baseUrl, token, getToken]);
  const priceMap = useMemo(() => {
    const entries = prices
      .filter((price): price is PlanPriceResponse & { tier: SignupTier } => isSignupTier(price.tier))
      .map((price) => [price.tier, price] as const);
    return Object.fromEntries(entries) as Partial<Record<SignupTier, PlanPriceResponse>>;
  }, [prices]);
  const stripePromise = useMemo(
    () => checkoutSession?.publishableKey ? loadStripe(checkoutSession.publishableKey) : null,
    [checkoutSession?.publishableKey],
  );
  const checkoutOptions = useMemo(
    () => checkoutSession
      ? {
          clientSecret: checkoutSession.clientSecret,
          onComplete: () => {
            setCheckoutSession(null);
            setStep("pending");
            setStatus({
              signupId: checkoutSession.signupId,
              selectedTier: checkoutSession.selectedTier,
              status: "payment_pending",
              tenantId: null,
              error: null,
            });
            void pollUntilReady(checkoutSession.signupId);
          },
        }
      : undefined,
    // pollUntilReady is intentionally called from the current render callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [checkoutSession],
  );
  const busy = actionLoading !== null;
  const signedIn = isAuthenticated || Boolean(token);
  const currentStepIndex = SIGNUP_STEPS.indexOf(step);

  const persistDraft = useCallback((overrides: Partial<SignupDraft> = {}) => {
    saveDraft({
      tenantName,
      tenantSlug,
      slugTouched,
      selectedTier,
      signupId,
      step,
      continueAfterSignIn,
      legalAccepted,
      ...overrides,
    });
  }, [continueAfterSignIn, legalAccepted, selectedTier, signupId, slugTouched, step, tenantName, tenantSlug]);

  const finishSignup = useCallback(async (ready: SignupReadyResponse | SignupStatusResponse) => {
    const newTenantId = ready.tenantId ?? null;
    if (!newTenantId) throw new Error("Tenant setup is not ready yet.");

    window.localStorage.setItem("activeTenantId", newTenantId);
    setActiveTenantId(newTenantId);
    if (!api) throw new Error("Sign-in is required before entering the new tenant.");
    await api.post("userinfo/login", {}, { tenantRequired: true, tenantId: newTenantId });
    clearDraft();
    navigate("/timeentry", { replace: true });
  }, [api, navigate, setActiveTenantId]);

  const goToPreviousStep = useCallback((targetStep: SignupStep) => {
    if (busy) return;
    const targetIndex = SIGNUP_STEPS.indexOf(targetStep);
    if (targetIndex < 0 || targetIndex >= currentStepIndex) return;
    if (targetStep === "details") {
      setSignupId(null);
      setCheckoutSession(null);
      setStatus(null);
    }
    if (targetStep === "plans") {
      setCheckoutSession(null);
    }
    setContinueAfterSignIn(false);
    setStep(targetStep);
    persistDraft({
      step: targetStep,
      signupId: targetStep === "details" ? null : signupId,
      continueAfterSignIn: false,
    });
  }, [busy, currentStepIndex, persistDraft, signupId]);

  const completeSignup = useCallback(async (id: string) => {
    if (!api) return null;
    const ready = await api.post<SignupReadyResponse>(
      "signup/complete",
      { signupId: id },
      { tenantRequired: false, headers: { "Idempotency-Key": `signup-complete-${id}` } },
    );
    if (ready?.status === "ready" && ready.tenantId) {
      await finishSignup(ready);
    }
    return ready;
  }, [api, finishSignup]);

  const pollUntilReady = useCallback(async (id: string) => {
    if (!api) return;
    setActionLoading("pending");
    setStep("pending");
    persistDraft({ signupId: id, step: "pending" });
    try {
      for (let attempt = 0; attempt < POLL_ATTEMPTS; attempt += 1) {
        if (attempt > 0) await delay(POLL_INTERVAL_MS);
        const next = await api.get<SignupStatusResponse>(`signup/status/${id}`, { tenantRequired: false });
        setStatus(next);
        if (next.status === "payment_confirmed" || next.status === "started") {
          try {
            const ready = await completeSignup(id);
            if (ready?.status === "ready") return;
          } catch (err) {
            const code = (err as { code?: string })?.code;
            if (code !== "SIGNUP_PAYMENT_PENDING") throw err;
          }
        }
        if (next.status === "ready" && next.tenantId) {
          await finishSignup(next);
          return;
        }
        if (next.status === "failed") {
          throw new Error(next.error || "Signup setup failed.");
        }
      }
      setError("Tenant setup is still processing. You can keep this page open or refresh to resume.");
    } catch (err) {
      setError(getErrorMessage(err, "Tenant setup could not be completed."));
    } finally {
      setActionLoading(null);
    }
  }, [api, completeSignup, finishSignup, persistDraft]);

  useEffect(() => {
    const draft = loadDraft();
    if (draft) {
      if (typeof draft.tenantName === "string") setTenantName(draft.tenantName);
      if (typeof draft.tenantSlug === "string") setTenantSlug(draft.tenantSlug);
      if (typeof draft.slugTouched === "boolean") setSlugTouched(draft.slugTouched);
      if (isSignupTier(draft.selectedTier)) setSelectedTier(draft.selectedTier);
      if (draft.continueAfterSignIn === true) setContinueAfterSignIn(true);
      if (draft.step === "plans" && slugify(draft.tenantName) && slugify(draft.tenantSlug)) {
        setStep("plans");
      }
      if (typeof draft.signupId === "string") {
        setSignupId(draft.signupId);
        if (draft.step === "checkout" || draft.step === "pending") setStep(draft.step);
      }
      if (typeof draft.legalAccepted === "boolean") setLegalAccepted(draft.legalAccepted);
    }

    const returnedSignupId = searchParams.get("signupId");
    if (returnedSignupId) {
      setSignupId(returnedSignupId);
      setStep("pending");
    }
    setHydrated(true);
  }, [searchParams]);

  useEffect(() => {
    if (!hydrated) return;
    persistDraft();
  }, [hydrated, persistDraft]);

  useEffect(() => {
    if (!hydrated || slugTouched) return;
    setTenantSlug(slugify(tenantName));
  }, [hydrated, slugTouched, tenantName]);

  useEffect(() => {
    let cancelled = false;
    setPricesLoading(true);
    setPricesError("");
    publicApi.get<PlanPriceResponse[]>("subscription/prices", { tenantRequired: false })
      .then((result) => {
        if (!cancelled) setPrices(Array.isArray(result) ? result : []);
      })
      .catch((err) => {
        if (!cancelled) setPricesError(getErrorMessage(err, "Plan pricing is unavailable."));
      })
      .finally(() => {
        if (!cancelled) setPricesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [publicApi]);

  useEffect(() => {
    const returnedSignupId = searchParams.get("signupId");
    const sessionId = searchParams.get("session_id");
    if (!api || !returnedSignupId || !sessionId) return;

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("checkout");
    nextParams.delete("session_id");
    setSearchParams(nextParams, { replace: true });

    setSignupId(returnedSignupId);
    setStep("pending");
    setActionLoading("checkout-return");
    api.post<SignupStatusResponse>(
      "signup/checkout/return",
      { signupId: returnedSignupId, sessionId },
      { tenantRequired: false },
    )
      .then((next) => {
        setStatus(next);
        void pollUntilReady(returnedSignupId);
      })
      .catch((err) => setError(getErrorMessage(err, "Checkout confirmation failed.")))
      .finally(() => setActionLoading(null));
  }, [api, pollUntilReady, searchParams, setSearchParams]);

  const startSignIn = useCallback(async (resumeSignup = false) => {
    setContinueAfterSignIn(resumeSignup);
    persistDraft({ continueAfterSignIn: resumeSignup });
    await instance.loginRedirect({
      ...loginRequest,
      prompt: "login",
      redirectStartPage: window.location.href,
    });
  }, [instance, persistDraft]);

  const signOutAndReturn = useCallback(async () => {
    persistDraft();
    await instance.logoutRedirect({ postLogoutRedirectUri: window.location.href });
  }, [instance, persistDraft]);

  const contactEnterpriseSales = useCallback(() => {
    window.open(
      "mailto:sales@landagility.com?subject=Enterprise%20Plan%20Inquiry",
      "_blank",
    );
    setError("Enterprise pricing is custom. Contact sales to set up an Enterprise tenant.");
  }, []);

  const resetSignup = useCallback(() => {
    clearDraft();
    setTenantName("");
    setTenantSlug("");
    setSlugTouched(false);
    setSelectedTier("pro");
    setSignupId(null);
    setLegalAccepted(false);
    setContinueAfterSignIn(false);
    setStep("details");
    setStatus(null);
    setCheckoutSession(null);
    setError("");
    saveDraft({
      tenantName: "",
      tenantSlug: "",
      slugTouched: false,
      selectedTier: "pro",
      signupId: null,
      step: "details",
      continueAfterSignIn: false,
      legalAccepted: false,
    });
  }, []);

  const validateDetails = useCallback(() => {
    const name = tenantName.trim();
    const rawSlug = tenantSlug.trim().toLowerCase();
    const slug = slugify(tenantSlug);
    if (!name) {
      setError("Tenant Name is required.");
      setStep("details");
      return false;
    }
    if (!slug) {
      setError("Tenant Slug is required.");
      setStep("details");
      return false;
    }
    if (rawSlug !== slug || slug.length < 3 || slug.length > 63) {
      setError("Tenant Slug must be 3-63 characters and contain only lowercase letters, numbers, and single dashes.");
      setStep("details");
      return false;
    }
    setTenantSlug(slug);
    setError("");
    return true;
  }, [tenantName, tenantSlug]);

  const startSignup = useCallback(async () => {
    if (!api) return null;
    if (!validateDetails()) return null;
    if (selectedTier === "enterprise") {
      setError("Enterprise tenants are set up through sales because pricing is custom.");
      return null;
    }
    if (!legalAccepted) {
      setError("You must accept the Terms of Service and Privacy Notice before creating a tenant.");
      setStep("plans");
      return null;
    }
    setActionLoading("start");
    try {
      const payload: SignupStartRequest = {
        tenantName: tenantName.trim(),
        tenantSlug: slugify(tenantSlug),
        selectedTier,
        enterpriseSalesFollowUpAcknowledged: false,
        legalAcceptance: {
          termsVersion: TERMS_VERSION,
          privacyVersion: PRIVACY_VERSION,
          accepted: true,
          source: "signup",
        },
      };
      const started = await api.post<SignupStartResponse>(
        "signup/start",
        payload,
        {
          tenantRequired: false,
          headers: { "Idempotency-Key": `signup-start-${slugify(tenantSlug)}-${selectedTier}` },
        },
      );
      if (!started.signupId) throw new Error("Signup did not return a signup id.");
      setSignupId(started.signupId);
      persistDraft({ signupId: started.signupId, continueAfterSignIn: false });
      return started;
    } catch (err) {
      setError(getErrorMessage(err, "Signup could not be started."));
      return null;
    } finally {
      setActionLoading(null);
    }
  }, [api, legalAccepted, persistDraft, selectedTier, tenantName, tenantSlug, validateDetails]);

  const startCheckout = useCallback(async (id: string) => {
    if (!api || !isPaidTier(selectedTier)) return;
    setActionLoading("checkout");
    try {
      const returnUrl = `${window.location.origin}/signup?checkout=complete&signupId=${id}&session_id={CHECKOUT_SESSION_ID}`;
      const result = await api.post<SignupCheckoutResponse>(
        "signup/checkout",
        { signupId: id, returnUrl },
        { tenantRequired: false, headers: { "Idempotency-Key": `signup-checkout-${id}` } },
      );
      if (!result.clientSecret || !result.publishableKey) {
        throw new Error("Checkout is not available yet. Please try again later.");
      }
      setCheckoutSession({
        clientSecret: result.clientSecret,
        publishableKey: result.publishableKey,
        signupId: result.signupId || id,
        selectedTier: result.selectedTier && isSignupTier(result.selectedTier) ? result.selectedTier : selectedTier,
        seatQuantity: result.seatQuantity ?? 1,
      });
      setStatus({
        signupId: result.signupId || id,
        selectedTier: result.selectedTier,
        status: result.status || "payment_pending",
        tenantId: null,
        error: null,
      });
      setStep("checkout");
      persistDraft({ signupId: result.signupId || id, step: "checkout", continueAfterSignIn: false });
    } catch (err) {
      setError(getErrorMessage(err, "Checkout could not be started."));
    } finally {
      setActionLoading(null);
    }
  }, [api, persistDraft, selectedTier]);

  const continueSignup = useCallback(async () => {
    if (!validateDetails()) return;
    if (step === "details") {
      setStep("plans");
      setError("");
      persistDraft({ step: "plans" });
      return;
    }
    if (selectedTier === "enterprise") {
      contactEnterpriseSales();
      return;
    }
    if (!signedIn || !token) {
      await startSignIn(step === "plans");
      return;
    }
    if (!api) return;

    const started = signupId ? { signupId } as SignupStartResponse : await startSignup();
    if (!started?.signupId) return;

    if (isPaidTier(selectedTier)) {
      await startCheckout(started.signupId);
      return;
    }

    setStep("pending");
    setStatus({
      signupId: started.signupId,
      selectedTier,
      status: "provisioning_tenant",
      tenantId: null,
      error: null,
    });
    setActionLoading("complete");
    try {
      await completeSignup(started.signupId);
    } catch (err) {
      setError(getErrorMessage(err, "Tenant setup could not be completed."));
    } finally {
      setActionLoading(null);
    }
  }, [api, completeSignup, contactEnterpriseSales, persistDraft, selectedTier, signedIn, signupId, startCheckout, startSignIn, startSignup, step, token, validateDetails]);

  useEffect(() => {
    if (!hydrated || !continueAfterSignIn || !signedIn || !api || step !== "plans" || actionLoading) return;
    if (pricesLoading && !pricesError) return;
    setContinueAfterSignIn(false);
    persistDraft({ continueAfterSignIn: false });
    void continueSignup();
  }, [actionLoading, api, continueAfterSignIn, continueSignup, hydrated, persistDraft, pricesError, pricesLoading, signedIn, step]);

  const currentStatus = status?.status || (checkoutSession ? "payment_pending" : step === "pending" ? "provisioning_tenant" : null);
  const setupSteps = [
    { key: "payment", label: "Processing payment", visible: isPaidTier(selectedTier), done: currentStatus && currentStatus !== "payment_pending" },
    { key: "tenant", label: "Creating tenant", visible: true, done: currentStatus === "seeding_defaults" || currentStatus === "assigning_owner" || currentStatus === "ready" },
    { key: "defaults", label: "Seeding default tenant data", visible: true, done: currentStatus === "assigning_owner" || currentStatus === "ready" },
    { key: "owner", label: "Adding you as Owner", visible: true, done: currentStatus === "ready" },
  ].filter((item) => item.visible);

  return (
    <div className={`main-articles ${styles["signup-page"]}`}>
      <div className={styles["signup-header"]}>
        <h2 className={styles["signup-title"]}>Create your LandAgility workspace</h2>
        <p className={styles["signup-subtitle"]}>
          Choose a plan, set up your tenant, and start with the right LandAgility tier from day one.
        </p>
      </div>

      {error && (
        <div className={styles["signup-alert"]} role="alert">
          <strong>Error</strong>
          <span>{error}</span>
        </div>
      )}

      <div className={styles["step-list"]} aria-label="Signup progress">
        {SIGNUP_STEPS.map((item, index) => {
          const canNavigateBack = index < currentStepIndex && !busy;
          return (
          <button
            type="button"
            key={item}
            className={`${styles["step-pill"]} ${step === item ? styles["step-pill--active"] : ""} ${canNavigateBack ? styles["step-pill--clickable"] : ""}`}
            onClick={() => goToPreviousStep(item)}
            disabled={!canNavigateBack}
            aria-current={step === item ? "step" : undefined}
          >
            <span>{index + 1}</span>
            {item === "details" ? "Tenant" : item === "plans" ? "Plan" : item === "checkout" ? "Payment" : "Setup"}
          </button>
          );
        })}
      </div>

      {step === "details" && (
      <section className={styles["signup-card"]}>
        <div className={styles["signup-card-header"]}>
          <span className={styles["signup-card-header-title"]}>Tenant details</span>
        </div>
        <div className={styles["signup-card-body"]}>
          <div className={styles["form-grid"]}>
            <div>
              <label htmlFor="tenantName" className={styles["field-label"]}>Tenant Name</label>
              <input
                id="tenantName"
                aria-label="Tenant Name"
                value={tenantName}
                onChange={(event) => setTenantName(event.target.value)}
                placeholder="Acme Land Services"
                disabled={busy}
                className={styles["text-input"]}
              />
            </div>
            <div>
              <label htmlFor="tenantSlug" className={styles["field-label"]}>Tenant Slug</label>
              <input
                id="tenantSlug"
                aria-label="Tenant Slug"
                value={tenantSlug}
                onChange={(event) => {
                  setSlugTouched(true);
                  setTenantSlug(event.target.value);
                }}
                placeholder="acme-land-services"
                disabled={busy}
                className={styles["text-input"]}
              />
              <div className={styles["field-hint"]}>Letters, numbers, and dashes only. Reserved for 24 hours after signup starts.</div>
            </div>
          </div>
        </div>
      </section>
      )}

      {step === "plans" && (
      <section className={styles["signup-card"]}>
        <div className={styles["signup-card-header"]}>
          <span className={styles["signup-card-header-title"]}>Choose a plan</span>
        </div>
        <div className={styles["signup-card-body"]}>
          {pricesLoading && <div className={styles["muted-text"]}>Loading plan pricing...</div>}
          {pricesError && <div className={styles["inline-error"]}>{pricesError}</div>}
          <div className={styles["plan-grid"]}>
            {TIER_ORDER.map((tier) => {
              const plan = priceMap[tier];
              const selected = selectedTier === tier;
              return (
                <button
                  type="button"
                  key={tier}
                  className={`${styles["plan-card"]} ${selected ? styles["plan-card--selected"] : ""}`}
                  onClick={() => {
                    setSelectedTier(tier);
                    if (tier !== selectedTier) {
                      setSignupId(null);
                      setCheckoutSession(null);
                      setStatus(null);
                      persistDraft({ selectedTier: tier, signupId: null });
                    }
                    setError("");
                  }}
                  disabled={busy}
                  aria-pressed={selected}
                  data-testid={`signup-plan-${tier}`}
                >
                  <span className={styles["plan-name"]}>{TIER_LABELS[tier]}</span>
                  <span className={styles["plan-price"]}>{formatPlanPrice(plan, tier, pricesLoading)}</span>
                  <span className={styles["plan-seat"]}>Per user: {formatSeatPrice(plan, tier, pricesLoading)}</span>
                  <span className={styles["plan-description"]}>{plan?.description || TIER_DESCRIPTIONS[tier]}</span>
                </button>
              );
            })}
          </div>

          {selectedTier === "enterprise" && (
            <div className={styles["ack-row"]}>
              Enterprise pricing is custom. Contact sales to review pricing, integrations, and implementation needs before
              an Enterprise tenant is created.
            </div>
          )}

          {selectedTier !== "enterprise" && (
            <label className={styles["legal-acceptance"]}>
              <input
                type="checkbox"
                checked={legalAccepted}
                onChange={(event) => {
                  setLegalAccepted(event.target.checked);
                  persistDraft({ legalAccepted: event.target.checked });
                  if (event.target.checked) setError("");
                }}
                disabled={busy}
              />
              <span>
                I am authorized to create this Land Agility workspace and accept
                the <Link to="/terms" target="_blank" rel="noopener noreferrer">Terms of Service</Link>
                {" "}and <Link to="/privacy" target="_blank" rel="noopener noreferrer">Privacy Notice</Link>
                {" "}on behalf of my organization.
              </span>
            </label>
          )}

          <div className={styles["comparison-shell"]}>
            <table className={styles["comparison-table"]}>
              <thead>
                <tr>
                  <th>Feature</th>
                  {TIER_ORDER.map((tier) => (
                    <th key={tier} className={selectedTier === tier ? styles["selected-col"] : ""}>
                      {TIER_LABELS[tier]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {PLAN_FEATURES.map((feature) => (
                  <tr key={feature.label}>
                    <td>{feature.label}</td>
                    {TIER_ORDER.map((tier) => (
                      <td key={tier} className={selectedTier === tier ? styles["selected-col"] : ""}>
                        {renderFeatureValue(feature[tier])}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>
      )}

      {step === "checkout" && checkoutSession && stripePromise && checkoutOptions && (
        <section className={styles["signup-card"]} id="signupCheckout" data-testid="signup-checkout-card">
          <div className={styles["signup-card-header"]}>
            <span className={styles["signup-card-header-title"]}>{TIER_LABELS[checkoutSession.selectedTier]} checkout</span>
          </div>
          <div className={styles["signup-card-body"]}>
            <p className={styles["muted-text"]}>
              Complete secure payment details below. Tenant setup starts after Stripe confirms payment.
            </p>
            <div className={styles["checkout-shell"]}>
              <EmbeddedCheckoutProvider stripe={stripePromise} options={checkoutOptions}>
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          </div>
        </section>
      )}

      {step === "pending" && (
        <section className={styles["signup-card"]} data-testid="signup-pending-card">
          <div className={styles["signup-card-header"]}>
            <span className={styles["signup-card-header-title"]}>Setting up your tenant</span>
          </div>
          <div className={styles["signup-card-body"]}>
            <p className={styles["muted-text"]}>
              We are processing your signup, creating the tenant, seeding default tenant data, and adding you as Owner.
            </p>
            <div className={styles["setup-list"]}>
              {setupSteps.map((item) => (
                <div key={item.key} className={styles["setup-row"]}>
                  <span className={`${styles["setup-dot"]} ${item.done ? styles["setup-dot--done"] : ""}`} />
                  <span>{item.label}</span>
                  <span className={styles["setup-status"]}>{item.done ? "Complete" : "Pending"}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      <div className={styles["action-bar"]}>
        <button
          type="button"
          className={styles["primary-btn"]}
          onClick={() => void continueSignup()}
          disabled={busy || step === "checkout" || step === "pending"}
        >
          {step === "details"
            ? "Continue to plan options"
            : !signedIn || !token
            ? "Sign in / Create account"
            : selectedTier === "enterprise"
              ? "Contact sales for Enterprise"
            : isPaidTier(selectedTier)
              ? actionLoading === "checkout" ? "Starting checkout..." : `Continue to ${TIER_LABELS[selectedTier]} checkout`
              : actionLoading ? "Creating tenant..." : `Create ${TIER_LABELS[selectedTier]} tenant`}
        </button>
        {(step === "plans" || step === "checkout") && (
          <button
            type="button"
            className={styles["secondary-btn"]}
            onClick={() => goToPreviousStep(step === "checkout" ? "plans" : "details")}
            disabled={busy}
          >
            Back
          </button>
        )}
        <button type="button" className={styles["secondary-btn"]} onClick={resetSignup} disabled={busy}>
          Reset
        </button>
        {signedIn && (
          <button type="button" className={styles["secondary-btn"]} onClick={() => void signOutAndReturn()} disabled={busy}>
            Sign out
          </button>
        )}
      </div>
    </div>
  );
};

export default Signup;
